import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Film, getFilmsByIds, getFilmById, getFilms } from '../api/film';
import { getBudgetsByFilmId } from '../api/budget';
import { getBreakdownListByFilmId } from '../api/breakdown';
import {
  getPermissionsBatchByResourceIDs,
  getPermissionsListForGranteeUser,
  groupUserPermissionsByResourceId,
  ResourceType,
} from '../api/userPermissions';
import { useBatchUsersQuery } from '../hooks/useBatchUsersQuery';
import { useUserPermissions } from './UserPermissionsContext';
import { useCurrentUser } from './CurrentUserContext';
import type { BudgetResponse, BreakdownInfo } from '../pages/Home/types';

export interface CurrentFilmBundle {
  film: Film;
  budgets: BudgetResponse[];
  breakdown: BreakdownInfo[];
}

export const SESSION_HOME_BOOTSTRAP_KEY = 'lineup_pendingHomeBootstrap';

function parseBudgetsResponse(data: unknown): BudgetResponse[] {
  if (!data) return [];
  if (Array.isArray(data)) return data as BudgetResponse[];
  if (Array.isArray((data as { data?: unknown }).data)) return (data as { data: BudgetResponse[] }).data;
  if ((data as { data?: BudgetResponse }).data) return [(data as { data: BudgetResponse }).data];
  return [];
}

interface FilmContextType {
  selectedFilmID: string | null;
  selectedFilmName: string | null;
  filmStartShootingDate: string | null;
  setSelectedFilm: (filmID: string, filmName: string, filmStartShootingDate?: string | null) => void;
  clearSelectedFilm: () => void;
  films: Film[];
  filmsLoading: boolean;
  setFilms: React.Dispatch<React.SetStateAction<Film[]>>;
  currentFilm: CurrentFilmBundle | null;
  currentFilmChildrenLoading: boolean;
  homeDataHydrated: boolean;
  bootstrapAfterLogin: () => Promise<void>;
  selectFilm: (filmId: string) => Promise<void>;
  refreshFilmListFromPermissions: () => Promise<Film[]>;
  appendSceneToCurrentFilm: (scene: BreakdownInfo) => void;
  appendBudgetToCurrentFilm: (budget: BudgetResponse) => void;
  /** 合并单条分场（如 PATCH 返回），不触发全量 load，避免主页整块 loading */
  patchSceneInCurrentFilm: (updated: BreakdownInfo) => void;
  reloadCurrentFilmChildren: () => Promise<void>;
  /** @deprecated 使用 refreshFilmListFromPermissions */
  loadFilms: () => Promise<void>;
  /** @deprecated 使用 refreshFilmListFromPermissions */
  refreshFilms: () => Promise<void>;
}

const FilmContext = createContext<FilmContextType | undefined>(undefined);

export const useFilmContext = () => {
  const context = useContext(FilmContext);
  if (context === undefined) {
    throw new Error('useFilmContext must be used within a FilmProvider');
  }
  return context;
};

export const FilmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setPermissions } = useUserPermissions();
  const { collectAndQueryUsers } = useBatchUsersQuery();
  const { userCache, getCurrentUserID, serverSyncedData, setServerSyncedField } = useCurrentUser();

  const [selectedFilmID, setSelectedFilmID] = useState<string | null>(() => localStorage.getItem('selectedFilmID'));
  const [selectedFilmName, setSelectedFilmName] = useState<string | null>(() =>
    localStorage.getItem('selectedFilmName')
  );
  const [filmStartShootingDate, setFilmStartShootingDate] = useState<string | null>(() =>
    localStorage.getItem('filmStartShootingDate')
  );

  const [films, setFilms] = useState<Film[]>([]);
  const [filmsLoading, setFilmsLoading] = useState(false);
  const [currentFilm, setCurrentFilm] = useState<CurrentFilmBundle | null>(null);
  const [currentFilmChildrenLoading, setCurrentFilmChildrenLoading] = useState(false);
  const [homeDataHydrated, setHomeDataHydrated] = useState(false);
  /** 防止快速切换影片时，较早完成的请求覆盖较晚选中影片的 currentFilm */
  const loadChildrenSeqRef = useRef(0);
  /** 防止 React StrictMode 等导致 bootstrapAfterLogin 并发执行两次、重复打爆后端 */
  const bootstrapPromiseRef = useRef<Promise<void> | null>(null);

  const setSelectedFilm = useCallback((filmID: string, filmName: string, filmStart?: string | null) => {
    setSelectedFilmID(filmID);
    setSelectedFilmName(filmName);
    setFilmStartShootingDate(filmStart || null);
    localStorage.setItem('selectedFilmID', filmID);
    localStorage.setItem('selectedFilmName', filmName);
    if (filmStart) localStorage.setItem('filmStartShootingDate', filmStart);
    else localStorage.removeItem('filmStartShootingDate');
  }, []);

  const clearSelectedFilm = useCallback(() => {
    setSelectedFilmID(null);
    setSelectedFilmName(null);
    setFilmStartShootingDate(null);
    localStorage.removeItem('selectedFilmID');
    localStorage.removeItem('selectedFilmName');
    localStorage.removeItem('filmStartShootingDate');
  }, []);

  /** 拉取当前用户对 FILM 的权限行写入 UserPermissionsContext，并与 owner 影片 ID 合并（无权限行的 owner 片写入 []） */
  const collectFilmIdsForUser = useCallback(async (): Promise<string[]> => {
    const userId = getCurrentUserID();
    let filmPermList: Awaited<ReturnType<typeof getPermissionsListForGranteeUser>> = [];
    try {
      filmPermList = await getPermissionsListForGranteeUser(userId, ResourceType.FILM);
    } catch (e) {
      console.warn('获取用户影片权限列表失败:', e);
    }
    const grouped = groupUserPermissionsByResourceId(filmPermList);
    const fromPermsIds = Array.from(grouped.keys());

    const ownerRes = await getFilms({ filmOwnerID: userId });
    const ownerIds = (ownerRes.data || []).map((f) => f.filmID);
    const allIds = Array.from(new Set([...fromPermsIds, ...ownerIds]));

    for (const id of allIds) {
      setPermissions(ResourceType.FILM, id, grouped.get(id) ?? []);
    }

    return allIds;
  }, [getCurrentUserID, setPermissions]);

  /** 仅批量预取影片相关用户信息（FILM 权限已在 collectFilmIdsForUser 写入 Context） */
  const prefetchFilmListUsers = useCallback(
    async (filmList: Film[]) => {
      if (filmList.length === 0) return;
      const filmIDs = filmList.map((f) => f.filmID);
      collectAndQueryUsers(filmList, 'Film', filmIDs).catch((error) => {
        console.warn('批量查询影片项目相关用户信息失败（不影响主流程）:', error);
      });
    },
    [collectAndQueryUsers]
  );

  const loadChildrenForFilm = useCallback(
    async (film: Film) => {
      const seq = ++loadChildrenSeqRef.current;
      setCurrentFilmChildrenLoading(true);
      try {
        const [budgetsRes, breakdownRes] = await Promise.all([
          getBudgetsByFilmId(film.filmID),
          getBreakdownListByFilmId(film.filmID),
        ]);
        if (seq !== loadChildrenSeqRef.current) return;

        const budgets = parseBudgetsResponse(budgetsRes.data);
        const breakdown = (breakdownRes.data || []) as BreakdownInfo[];

        const breakdownIDs = breakdown.map((s) => s.breakdownID);
        const budgetIDs = budgets.map((b) => b.budgetID);
        if (breakdownIDs.length > 0) {
          const m = await getPermissionsBatchByResourceIDs(ResourceType.BREAKDOWN, breakdownIDs);
          if (seq !== loadChildrenSeqRef.current) return;
          m.forEach((perms, id) => setPermissions(ResourceType.BREAKDOWN, id, perms));
        }
        if (budgetIDs.length > 0) {
          const m = await getPermissionsBatchByResourceIDs(ResourceType.BUDGET, budgetIDs);
          if (seq !== loadChildrenSeqRef.current) return;
          m.forEach((perms, id) => setPermissions(ResourceType.BUDGET, id, perms));
        }
        collectAndQueryUsers(breakdown, 'Breakdown', breakdownIDs).catch(() => {});
        collectAndQueryUsers(budgets, 'Budget', budgetIDs).catch(() => {});

        if (seq !== loadChildrenSeqRef.current) return;
        setCurrentFilm({ film, budgets, breakdown });
      } catch (err) {
        if (seq !== loadChildrenSeqRef.current) return;
        console.error('加载当前影片分场/预算失败:', err);
        setCurrentFilm({ film, budgets: [], breakdown: [] });
      } finally {
        if (seq === loadChildrenSeqRef.current) {
          setCurrentFilmChildrenLoading(false);
        }
      }
    },
    [setPermissions, collectAndQueryUsers]
  );

  const refreshFilmListFromPermissions = useCallback(async (): Promise<Film[]> => {
    try {
      setFilmsLoading(true);
      const filmIds = await collectFilmIdsForUser();
      if (filmIds.length === 0) {
        setFilms([]);
        return [];
      }
      const response = await getFilmsByIds(filmIds);
      const filmList = response.data || [];
      setFilms(filmList);
      await prefetchFilmListUsers(filmList);
      return filmList;
    } catch (err) {
      console.error('refreshFilmListFromPermissions failed:', err);
      return [];
    } finally {
      setFilmsLoading(false);
    }
  }, [collectFilmIdsForUser, prefetchFilmListUsers]);

  const resolveTargetFilmId = useCallback(
    (filmList: Film[]): string | null => {
      const last = serverSyncedData.userLastUsedFilmID;
      if (last && filmList.some((f) => f.filmID === last)) return last;
      if (filmList.length === 0) return null;
      const sorted = [...filmList].sort((a, b) => {
        const timeA = a.filmUpdateTime ? new Date(a.filmUpdateTime).getTime() : 0;
        const timeB = b.filmUpdateTime ? new Date(b.filmUpdateTime).getTime() : 0;
        return timeB - timeA;
      });
      return sorted[0]?.filmID || null;
    },
    [serverSyncedData.userLastUsedFilmID]
  );

  const bootstrapAfterLogin = useCallback(async () => {
    if (bootstrapPromiseRef.current) {
      await bootstrapPromiseRef.current;
      return;
    }

    const run = async () => {
      try {
        setFilmsLoading(true);
        const filmIds = await collectFilmIdsForUser();
        if (filmIds.length === 0) {
          setFilms([]);
          setCurrentFilm(null);
          clearSelectedFilm();
          setHomeDataHydrated(true);
          return;
        }
        const response = await getFilmsByIds(filmIds);
        let filmList = response.data || [];
        setFilms(filmList);
        await prefetchFilmListUsers(filmList);

        const targetId = resolveTargetFilmId(filmList);
        if (!targetId) {
          setHomeDataHydrated(true);
          return;
        }
        let film = filmList.find((f) => f.filmID === targetId);
        if (!film) {
          const one = await getFilmById(targetId);
          film = one.data;
        }
        if (film) {
          setSelectedFilm(film.filmID, film.filmName, film.filmStartShootingDate || null);
          await loadChildrenForFilm(film);
        }
      } catch (err) {
        console.error('bootstrapAfterLogin failed:', err);
      } finally {
        setFilmsLoading(false);
        setHomeDataHydrated(true);
      }
    };

    const p = run().finally(() => {
      if (bootstrapPromiseRef.current === p) {
        bootstrapPromiseRef.current = null;
      }
    });
    bootstrapPromiseRef.current = p;
    await p;
  }, [
    collectFilmIdsForUser,
    prefetchFilmListUsers,
    resolveTargetFilmId,
    loadChildrenForFilm,
    setSelectedFilm,
    clearSelectedFilm,
  ]);

  const selectFilm = useCallback(
    async (filmId: string) => {
      let film = films.find((f) => f.filmID === filmId);
      if (!film) {
        try {
          const res = await getFilmById(filmId);
          film = res.data;
        } catch (e) {
          console.error('selectFilm getFilmById failed:', e);
          return;
        }
      }
      if (!film) return;
      setSelectedFilm(film.filmID, film.filmName, film.filmStartShootingDate || null);
      setServerSyncedField('userLastUsedFilmID', film.filmID);
      await loadChildrenForFilm(film);
    },
    [films, setSelectedFilm, setServerSyncedField, loadChildrenForFilm]
  );

  const loadFilms = useCallback(async () => {
    await refreshFilmListFromPermissions();
  }, [refreshFilmListFromPermissions]);

  const reloadCurrentFilmChildren = useCallback(async () => {
    const id = selectedFilmID;
    if (!id) return;
    try {
      let film = films.find((f) => f.filmID === id);
      if (!film) {
        const res = await getFilmById(id);
        film = res.data;
      }
      if (film) await loadChildrenForFilm(film);
    } catch (e) {
      console.warn('reloadCurrentFilmChildren failed:', e);
    }
  }, [selectedFilmID, films, loadChildrenForFilm]);

  const refreshFilms = useCallback(async () => {
    await refreshFilmListFromPermissions();
    const id = selectedFilmID;
    if (!id) return;
    try {
      const res = await getFilmById(id);
      if (res.data) await loadChildrenForFilm(res.data);
    } catch (e) {
      console.warn('refreshFilms reload children failed:', e);
    }
  }, [refreshFilmListFromPermissions, selectedFilmID, loadChildrenForFilm]);

  const appendSceneToCurrentFilm = useCallback((scene: BreakdownInfo) => {
    setCurrentFilm((prev) => {
      if (!prev) return prev;
      return { ...prev, breakdown: [...prev.breakdown, scene] };
    });
  }, []);

  const appendBudgetToCurrentFilm = useCallback((budget: BudgetResponse) => {
    setCurrentFilm((prev) => {
      if (!prev) return prev;
      return { ...prev, budgets: [...prev.budgets, budget] };
    });
  }, []);

  const patchSceneInCurrentFilm = useCallback((updated: BreakdownInfo) => {
    setCurrentFilm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        breakdown: prev.breakdown.map((s) =>
          s.breakdownID === updated.breakdownID ? { ...s, ...updated } : s
        ),
      };
    });
  }, []);

  useEffect(() => {
    if (!userCache?.user) {
      setFilms([]);
      setCurrentFilm(null);
      setHomeDataHydrated(false);
      clearSelectedFilm();
    }
  }, [userCache, clearSelectedFilm]);

  const value: FilmContextType = {
    selectedFilmID,
    selectedFilmName,
    filmStartShootingDate,
    setSelectedFilm,
    clearSelectedFilm,
    films,
    filmsLoading,
    setFilms,
    currentFilm,
    currentFilmChildrenLoading,
    homeDataHydrated,
    bootstrapAfterLogin,
    selectFilm,
    refreshFilmListFromPermissions,
    appendSceneToCurrentFilm,
    appendBudgetToCurrentFilm,
    patchSceneInCurrentFilm,
    reloadCurrentFilmChildren,
    loadFilms,
    refreshFilms,
  };

  return <FilmContext.Provider value={value}>{children}</FilmContext.Provider>;
};
